import './gestapeld-woningaanbod.scss';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';

gsap.registerPlugin( Draggable, InertiaPlugin );

const VISIBLE = 5;
const CENTER = 2;

function getStates( track ) {
	const tw = track.offsetWidth;
	const bh = track.offsetHeight;

	if ( window.innerWidth <= 768 ) {
		const cw = Math.round( tw * 0.72 );
		const sw = Math.round( tw * 0.5 );
		const cl = Math.round( ( tw - cw ) / 2 );
		const peek = Math.round( tw * 0.18 );

		return [
			{ left: peek - sw * 2, width: sw, height: Math.round( bh * 0.55 ), zIndex: 1, opacity: 0, brightness: 0.6, label: false },
			{ left: peek - sw, width: sw, height: Math.round( bh * 0.78 ), zIndex: 3, opacity: 1, brightness: 0.72, label: false },
			{ left: cl, width: cw, height: bh, zIndex: 5, opacity: 1, brightness: 1, label: true },
			{ left: tw - peek, width: sw, height: Math.round( bh * 0.78 ), zIndex: 3, opacity: 1, brightness: 0.72, label: false },
			{ left: tw - peek + sw, width: sw, height: Math.round( bh * 0.55 ), zIndex: 1, opacity: 0, brightness: 0.6, label: false },
		];
	}

	const cw = Math.round( bh * 17 / 20 );
	const sw1 = Math.round( cw * 0.7 );
	const sw2 = Math.round( cw * 0.56 );
	const cl = Math.round( ( tw - cw ) / 2 );
	const ov1 = Math.round( sw1 * 0.28 );
	const ov2 = Math.round( sw2 * 0.22 );

	return [
		{ left: cl - sw1 + ov1 - sw2 + ov2, width: sw2, height: Math.round( bh * 0.6 ), zIndex: 1, opacity: 1, brightness: 0.6, label: false },
		{ left: cl - sw1 + ov1, width: sw1, height: Math.round( bh * 0.8 ), zIndex: 3, opacity: 1, brightness: 0.78, label: false },
		{ left: cl, width: cw, height: bh, zIndex: 5, opacity: 1, brightness: 1, label: true },
		{ left: cl + cw - ov1, width: sw1, height: Math.round( bh * 0.8 ), zIndex: 3, opacity: 1, brightness: 0.78, label: false },
		{ left: cl + cw - ov1 + sw1 - ov2, width: sw2, height: Math.round( bh * 0.6 ), zIndex: 1, opacity: 1, brightness: 0.6, label: false },
	];
}

function updateSlider( currentCards, states, immediate, customDuration, tl, position ) {
	const duration = immediate ? 0 : ( customDuration || 0.8 );
	const engine = tl || gsap;

	currentCards.forEach( ( card, index ) => {
		const state = states[ index ];
		const isActive = index === CENTER;

		if ( ! state ) {
			gsap.to( card, {
				autoAlpha: 0,
				duration: immediate ? 0 : 0.6,
				ease: 'power3.inOut',
			} );
			return;
		}

		const label = card.querySelector( '.waabi-label' );
		const badge = card.querySelector( '.b-gestapeldWoningaanbod__badge' );
		const image = card.querySelector( 'img' );

		engine.to( card, {
			left: state.left,
			width: state.width,
			height: state.height,
			zIndex: state.zIndex,
			autoAlpha: state.opacity,
			scale: 1,
			top: '50%',
			yPercent: -50,
			y: 0,
			transformOrigin: 'center center',
			duration: duration,
			ease: 'expo.out',
			overwrite: 'auto',
		}, position );

		if ( image ) {
			engine.to( image, {
				filter: 'brightness(' + state.brightness + ')',
				duration: duration,
				ease: 'expo.out',
			}, position );
		}

		if ( label ) {
			engine.to( label, {
				autoAlpha: isActive ? 1 : 0,
				duration: immediate ? 0 : 0.4,
			}, position );
		}

		if ( badge ) {
			engine.to( badge, {
				autoAlpha: isActive ? 1 : 0,
				duration: immediate ? 0 : 0.4,
			}, position );
		}
	} );
}

function createGhost( exitingCard, track ) {
	const ghost = exitingCard.cloneNode( true );

	const ghostLabel = ghost.querySelector( '.waabi-label' );
	if ( ghostLabel ) {
		ghostLabel.style.display = 'none';
	}

	const ghostBadge = ghost.querySelector( '.b-gestapeldWoningaanbod__badge' );
	if ( ghostBadge ) {
		ghostBadge.style.display = 'none';
	}

	track.appendChild( ghost );

	gsap.set( ghost, {
		position: 'absolute',
		left: gsap.getProperty( exitingCard, 'left' ),
		width: gsap.getProperty( exitingCard, 'width' ),
		height: gsap.getProperty( exitingCard, 'height' ),
		autoAlpha: gsap.getProperty( exitingCard, 'autoAlpha' ),
		top: '50%',
		yPercent: -50,
		y: 0,
		borderRadius: '12px',
		overflow: 'hidden',
		transformOrigin: 'center center',
	} );

	const ghostImage = ghost.querySelector( 'img' );
	if ( ghostImage ) {
		const existingImage = exitingCard.querySelector( 'img' );
		const currentFilter = existingImage ? gsap.getProperty( existingImage, 'filter' ) : 'brightness(0.6)';
		gsap.set( ghostImage, { filter: currentFilter } );
	}

	return ghost;
}

function initGestapeldWoningaanbod() {
	const roots = document.querySelectorAll( '[data-component="gestapeld-woningaanbod"]' );

	if ( ! roots.length ) {
		return;
	}

	roots.forEach( ( root ) => {
		const slider = root.querySelector( '[data-stacked-slider]' );
		const track = root.querySelector( '[data-stacked-track]' );
		const cards = Array.from( root.querySelectorAll( '[data-stacked-card]' ) );

		if ( ! slider || ! track || cards.length < VISIBLE ) {
			return;
		}

		let states = getStates( track );
		let currentCards = [ ...cards ];
		let intervalId = null;
		let isRotating = false;
		let draggableInstance = null;

		function resetTimer() {
			clearInterval( intervalId );
			intervalId = setInterval( rotate, 4000 );
		}

		function rotate( duration ) {
			duration = duration || 0.8;

			if ( isRotating ) {
				return;
			}
			isRotating = true;
			clearInterval( intervalId );

			const exitingCard = currentCards.shift();

			const exitingLabel = exitingCard.querySelector( '.waabi-label' );
			if ( exitingLabel ) {
				gsap.set( exitingLabel, { autoAlpha: 0 } );
			}
			const exitingBadge = exitingCard.querySelector( '.b-gestapeldWoningaanbod__badge' );
			if ( exitingBadge ) {
				gsap.set( exitingBadge, { autoAlpha: 0 } );
			}

			const ghost = createGhost( exitingCard, track );
			gsap.set( ghost, { zIndex: 0 } );

			gsap.to( ghost, {
				scale: 0.5,
				autoAlpha: 0,
				duration: 0.8,
				ease: 'expo.inOut',
				onComplete: () => ghost.remove(),
			} );

			currentCards.push( exitingCard );

			const trackWidth = track.offsetWidth;
			gsap.set( exitingCard, {
				left: trackWidth + 100,
				scale: 1,
				autoAlpha: 0,
				width: states[ VISIBLE - 1 ].width,
				height: states[ VISIBLE - 1 ].height,
				top: '50%',
				yPercent: -50,
				y: 0,
				transformOrigin: 'center center',
			} );

			const exitingImage = exitingCard.querySelector( 'img' );
			if ( exitingImage ) {
				gsap.set( exitingImage, {
					filter: 'brightness(' + states[ VISIBLE - 1 ].brightness + ')',
				} );
			}

			updateSlider( currentCards, states, false, duration );

			gsap.delayedCall( duration, () => {
				isRotating = false;
				resetTimer();
			} );
		}

		function rotateBack( duration ) {
			duration = duration || 0.8;

			if ( isRotating ) {
				return;
			}
			isRotating = true;
			clearInterval( intervalId );

			const exitingCard = currentCards.pop();

			const ghost = createGhost( exitingCard, track );
			gsap.set( ghost, { zIndex: states[ VISIBLE - 1 ].zIndex } );

			const trackWidth = track.offsetWidth;
			gsap.to( ghost, {
				left: trackWidth + 200,
				autoAlpha: 0,
				duration: duration,
				ease: 'expo.out',
				onComplete: () => ghost.remove(),
			} );

			currentCards.unshift( exitingCard );

			gsap.set( exitingCard, {
				left: -states[ 0 ].width - 100,
				autoAlpha: 0,
				width: states[ 0 ].width,
				height: states[ 0 ].height,
				zIndex: states[ 0 ].zIndex,
				top: '50%',
				yPercent: -50,
				y: 0,
				transformOrigin: 'center center',
			} );

			const enteringImage = exitingCard.querySelector( 'img' );
			if ( enteringImage ) {
				gsap.set( enteringImage, {
					filter: 'brightness(' + states[ 0 ].brightness + ')',
				} );
			}

			updateSlider( currentCards, states, false, duration );

			gsap.delayedCall( duration, () => {
				isRotating = false;
				resetTimer();
			} );
		}

		function navigateTo( card ) {
			const index = currentCards.indexOf( card );
			if ( index === CENTER ) {
				return;
			}

			clearInterval( intervalId );
			if ( isRotating ) {
				return;
			}

			if ( index < CENTER ) {
				const steps = CENTER - index;
				const dur = steps > 1 ? 0.35 : 0.8;
				rotateBack( dur );
				if ( steps > 1 ) {
					gsap.delayedCall( dur + 0.05, () => navigateTo( card ) );
				}
			} else {
				const steps = index - CENTER;
				const dur = steps > 1 ? 0.35 : 0.8;
				rotate( dur );
				if ( steps > 1 ) {
					gsap.delayedCall( dur + 0.05, () => navigateTo( card ) );
				}
			}
		}

		cards.forEach( ( card ) => {
			const image = card.querySelector( 'img' );
			if ( ! image ) {
				return;
			}

			card.addEventListener( 'mouseenter', () => {
				clearInterval( intervalId );
				gsap.to( image, {
					filter: 'brightness(1)',
					duration: 0.4,
					ease: 'power2.out',
					overwrite: 'auto',
				} );
			} );

			card.addEventListener( 'mouseleave', () => {
				resetTimer();
				const cardIndex = currentCards.indexOf( card );
				const state = states[ cardIndex ];
				if ( state ) {
					gsap.to( image, {
						filter: 'brightness(' + state.brightness + ')',
						duration: 0.4,
						ease: 'power2.out',
						overwrite: 'auto',
					} );
				}
			} );
		} );

		updateSlider( currentCards, states, true );
		resetTimer();

		cards.forEach( ( card ) => {
			card.addEventListener( 'click', () => navigateTo( card ) );
		} );

		const activeCard = currentCards[ CENTER ];
		if ( activeCard ) {
			const elements = [
				activeCard.querySelector( '.waabi-tags' ),
				activeCard.querySelector( 'h3' ),
				activeCard.querySelector( '.waabi-address' ),
				activeCard.querySelector( '.waabi-price' ),
			].filter( ( el ) => el );

			gsap.fromTo( elements,
				{ y: 30, autoAlpha: 0 },
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.8,
					stagger: 0.1,
					ease: 'power3.out',
					delay: 0.5,
				}
			);
		}

		const proxy = document.createElement( 'div' );

		const draggables = Draggable.create( proxy, {
			trigger: slider,
			type: 'x',
			inertia: true,
			edgeResistance: 0.65,
			onPress: () => {
				clearInterval( intervalId );
				if ( ! isRotating ) {
					gsap.killTweensOf( currentCards );
				}
			},
			onDragEnd() {
				if ( isRotating ) {
					gsap.set( proxy, { x: 0 } );
					return;
				}

				const dragThreshold = 50;
				const endX = this.endX;

				if ( endX < -dragThreshold ) {
					rotate();
				} else if ( endX > dragThreshold ) {
					rotateBack();
				}

				gsap.set( proxy, { x: 0 } );
			},
		} );

		draggableInstance = draggables[ 0 ];

		const handleResize = () => {
			states = getStates( track );
			updateSlider( currentCards, states, true );
		};
		window.addEventListener( 'resize', handleResize );

		root._gestapeldWoningaanbodCleanup = () => {
			clearInterval( intervalId );
			window.removeEventListener( 'resize', handleResize );
			if ( draggableInstance ) {
				draggableInstance.kill();
			}
		};
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initGestapeldWoningaanbod );
} else {
	initGestapeldWoningaanbod();
}
